import { useCallback } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { FxRateOverride } from '../../../core/api/consolidation/types';
import { DailyFxRatesOverrideFormValues } from '../types';
import * as validation from './validation';

const overrideDefaultValues = {
  currencyFrom: '',
  currencyTo: '',
  from: undefined,
  to: undefined,
  dailyRate: ''
};

export const useDailyFxRatesOverrideForm = (currencyCode: string, presentationCurrency: string) => {
  const form = useForm<DailyFxRatesOverrideFormValues>({
    mode: 'onChange',
    defaultValues: {
      overrides: [{ ...overrideDefaultValues }]
    }
  });
  const { control, formState, getValues, reset, setValue } = form;

  const { append, remove, fields: overrideFields } = useFieldArray({
    control,
    name: 'overrides' // unique name for your Field Array
  });

  const addNewOverride = useCallback(() => {
    append({ ...overrideDefaultValues, currencyFrom: currencyCode, currencyTo: presentationCurrency });
  }, [append, currencyCode, presentationCurrency]);

  const checkForOverlappingDateRanges = () => {
    const formData = getValues();
    const values = formData.overrides.map(o => ({ from: o.dateFrom, to: o.dateTo }));
    return validation.checkForOverlappingDateRanges(values);
  };

  const restore = useCallback(
    (restoreData: DailyFxRatesOverrideFormValues) => {
      reset();
      setValue('overrides', restoreData.overrides);
      if (!restoreData.overrides.length) {
        addNewOverride();
      }
      return getValues();
    },
    [addNewOverride, getValues, reset, setValue]
  );

  const restoreConfig = useCallback(
    (configData: FxRateOverride[]) => {
      reset();
      setValue('overrides', [...configData]);
      return getValues();
    },
    [getValues, reset, setValue]
  );

  return {
    addNewOverride,
    appendOverride: append,
    checkForOverlappingDateRanges,
    control,
    formState,
    getValues,
    overrideFields,
    removeOverride: remove,
    reset,
    restore,
    restoreConfig,
    setValue
  };
};
